import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Home as HomeIcon, Heart, Brain, Star, MapPin, ArrowRight, ChevronRight, MessageSquareText, Users, Handshake, BookOpen } from 'lucide-react';
import SearchBar from '@/components/SearchBar';
import api from '@/lib/api';

const Home = () => {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [blogArticles, setBlogArticles] = useState([]);
  const [latestEpisode, setLatestEpisode] = useState(null);
  const [latestEditorial, setLatestEditorial] = useState(null);

  useEffect(() => {
    api.get('/providers?featured=true').then(res => {
      const data = res.data.results || res.data;
      const sorted = Array.isArray(data) ? data.sort((a, b) => (b.rating || 0) - (a.rating || 0)) : [];
      setFeatured(sorted);
    }).catch(() => {});
    api.get('/blog/articles?limit=20').then(res => {
      const articles = Array.isArray(res.data) ? res.data : [];

      // Exclude Editorial articles from Actualidad Senior section
      const filtered = articles
        .filter(a => a.category !== 'Editorial')
        .slice(0, 6);

      setBlogArticles(filtered);

      // Último Editorial publicado que tenga video
      const latestEditorialWithVideo = articles
        .filter(a => a.category === 'Editorial' && a.youtube_url)
        .sort((a, b) => {
          const dateA = new Date(a.published_at || a.created_at || 0);
          const dateB = new Date(b.published_at || b.created_at || 0);
          return dateB - dateA;
        })[0];

      if (latestEditorialWithVideo) {
        setLatestEditorial(latestEditorialWithVideo);
      }
    }).catch(() => {});
    api.get('/podcast/episodes').then(res => {
      const eps = res.data;
      if (Array.isArray(eps) && eps.length > 0) setLatestEpisode(eps[0]);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#00e7ff]/60 pt-16 pb-12 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto" data-testid="hero-section">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#33404f] leading-tight mb-6">
              Encuentra el mejor
              <span className="block text-[#33404f]">cuidado</span>
              para adultos mayores
            </h1>
            <p className="text-sm text-gray-500 max-w-2xl mx-auto mb-10">
              Conectamos familias con <span className="font-bold text-[#33404f]">residencias</span> y <span className="font-bold">cuidado a domicilio</span> de confianza.
            </p>
          </div>

          {/* SearchBar - ancho completo */}
          <div className="mb-8">
            <SearchBar />
          </div>
        </div>
      </section>

      {/* Residencias Destacadas */}
      {featured.length > 0 && (
        <FeaturedGrid featured={featured} />
      )}

      {/* Senior Market - Banner */}
      <section
        className="bg-white py-6 md:py-8"
        data-testid="senior-market-banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <a
            href="/seniormarket"
            className="block w-full overflow-hidden rounded-[24px] md:rounded-[30px] transition-transform duration-300 hover:scale-[1.01]"
            aria-label="Ver más sobre Senior Market"
          >
            <img
              src="/mercado-urbano-en-familia.jpg"
              alt="Senior Market - Mercado urbano para disfrutar en familia"
              className="block w-full h-auto"
              loading="lazy"
            />
          </a>
        </div>
      </section>

      {/* SeniorPodcast - Section */}
      <section className="py-20 bg-gray-50" data-testid="podcast-home-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Video - ultimo episodio */}
            <div className="flex justify-center">
              <div className="relative w-full" style={{ maxWidth: '500px' }}>
                <div className="aspect-video rounded-2xl shadow-lg overflow-hidden">
                  {latestEpisode?.youtube_url ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${latestEpisode.youtube_url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([^?&\s]+)/)?.[1] || ''}`}
                      title={latestEpisode.title}
                      className="w-full h-full"
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div className="w-full h-full bg-[#33404f] flex items-center justify-center">
                      <img src="/logo-senior-podcast.svg" alt="SeniorPodcast" className="h-16 opacity-50" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Text Content */}
            <div className="space-y-3">
              <img src="/logo-senior-podcast.svg" alt="SeniorPodcast" className="h-20" style={{ filter: 'brightness(0) saturate(100%) invert(0.2)' }} />
              <h2 className="text-3xl md:text-4xl font-bold text-[#33404f]">Ve nuestros podcast sobre bienestar y actualidad Senior</h2>
              <p className="text-gray-500 text-sm">Conversaciones, entrevistas y contenido exclusivo para adultos mayores y sus familias</p>
              <div className="pt-3">
                <Link to="/podcast">
                  <Button className="bg-[#00e7ff] hover:bg-[#00d4e8] text-[#33404f] font-bold px-6 py-3 rounded-xl text-sm">
                    Ver Podcast <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Como usar SeniorAdvisor */}
      <section className="bg-[#00e7ff]" data-testid="cta-banner">
        <div className="flex flex-col md:flex-row items-stretch">
          <div className="md:w-4/12 relative min-h-[350px] md:min-h-0">
            <img
              src="https://SeniorAdvisor.cl/como-usar-senior.jpeg"
              alt="Cómo usar SeniorAdvisor"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </div>
          <div className="md:w-8/12 flex items-center px-8 py-14 md:py-16 md:px-12">
              <div className="text-[#33404f] w-full">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
                  ¿Cómo usar SeniorAdvisor?
                </h2>
                <p className="text-lg md:text-xl leading-relaxed mb-10 text-center">
                  Encuentra, evalúa y comparte tu experiencia en simples pasos:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 rounded-full border-2 border-[#33404f] bg-[#00e7ff] flex items-center justify-center mb-3">
                      <MapPin className="w-9 h-9 text-[#33404f]" strokeWidth={2.5} />
                    </div>
                    <p className="font-bold text-2xl md:text-3xl mb-2">Busca un servicio</p>
                    <p className="text-[#33404f]/80 text-base md:text-lg leading-relaxed">Explora los distintos servicios, y ayúdanos a filtrar los mejores.</p>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 rounded-full border-2 border-[#33404f] bg-[#00e7ff] flex items-center justify-center mb-3">
                      <MessageSquareText className="w-9 h-9 text-[#33404f]" strokeWidth={2.5} />
                    </div>
                    <p className="font-bold text-2xl md:text-3xl mb-2">Deja tu reseña</p>
                    <p className="text-[#33404f]/80 text-base md:text-lg leading-relaxed">Escribe tu experiencia y otorga una valoración con estrellas.</p>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 rounded-full border-2 border-[#33404f] bg-[#00e7ff] flex items-center justify-center mb-3">
                      <Users className="w-9 h-9 text-[#33404f]" strokeWidth={2.5} />
                    </div>
                    <p className="font-bold text-2xl md:text-3xl mb-2">Ayuda a otros</p>
                    <p className="text-[#33404f]/80 text-base md:text-lg leading-relaxed">Tu opinión orienta a más personas a tomar una mejor decisión.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </section>

      {/* SeniorClub - Banner CTA */}
      <section className="py-12 bg-white" data-testid="senior-club-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/seniorclub" className="block">
            <div className="bg-[#33404f] rounded-2xl hover:bg-[#3a4a5c] transition-colors cursor-pointer group overflow-hidden">
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10 px-8 py-8 md:py-6">
                <div className="shrink-0">
                  <img
                    src="/seniorclub-logo.svg"
                    alt="SeniorClub"
                    className="h-16 md:h-20"
                    style={{ filter: 'brightness(0) invert(1)' }}
                  />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-white text-xl md:text-2xl font-bold mb-1">Convenios exclusivos para el bienestar de nuestros mayores</h3>
                  <p className="text-gray-300 text-sm">Accede a descuentos y servicios preferenciales con nuestros aliados</p>
                </div>
                <div className="shrink-0">
                  <div className="bg-[#00e7ff] hover:bg-[#00d4e8] text-[#33404f] font-bold px-6 py-3 rounded-xl text-sm group-hover:scale-105 transition-transform flex items-center gap-2">
                    Ver Convenios <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Video */}
            <div className="flex justify-center">
              <div className="relative w-full" style={{ maxWidth: '500px' }}>
                <div className="aspect-video rounded-2xl shadow-lg overflow-hidden">
                  <iframe
                    src="https://www.youtube.com/embed/2kU-gj4jHtc?si=fvILhF75aJ-EARqc"
                    title="SeniorAdvisor"
                    className="w-full h-full"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
            {/* Text Content */}
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-[#33404f]">Sobre Nosotros</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
                <p><span className="font-bold text-[#00e7ff]">SeniorAdvisor</span> es una <span className="font-semibold">plataforma innovadora</span> diseñada para ayudar a las familias a encontrar el mejor <span className="font-semibold text-[#00e7ff]">cuidado para adultos mayores</span>.</p>
                <p>Conectamos a familias con <span className="font-semibold">residencias de calidad</span> y <span className="font-semibold text-[#00e7ff]">cuidado a domicilio</span> verificados y confiables.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial Senior - Home Section */}
      <section
        className="py-20 bg-[#00e7ff]"
        data-testid="editorial-home-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Video - último Editorial */}
            <div className="flex justify-center">
              <div
                className="relative w-full"
                style={{ maxWidth: '500px' }}
              >
                <div className="aspect-video rounded-2xl shadow-lg overflow-hidden bg-[#33404f]">
                  {latestEditorial?.youtube_url ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${
                        latestEditorial.youtube_url.match(
                          /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([^?&\s]+)/
                        )?.[1] || ''
                      }`}
                      title={latestEditorial.title || 'Editorial Senior'}
                      className="w-full h-full"
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <img
                        src="/logo-editorialsenor.svg"
                        alt="Editorial Senior"
                        className="h-16 opacity-40"
                        style={{
                          filter:
                            'brightness(0) saturate(100%) invert(22%) sepia(14%) saturate(1027%) hue-rotate(169deg) brightness(91%) contrast(88%)'
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Contenido */}
            <div className="space-y-4">
              <img
                src="/logo-editorialsenor.svg"
                alt="Editorial Senior"
                className="h-[54px] w-auto"
                style={{
                  filter:
                    'brightness(0) saturate(100%) invert(22%) sepia(14%) saturate(1027%) hue-rotate(169deg) brightness(91%) contrast(88%)'
                }}
              />

              <h2 className="text-3xl md:text-4xl font-bold text-[#33404f] leading-tight">
                Reflexiones y contenidos para una mejor vida Senior
              </h2>

              <p className="text-[#33404f]/75 text-sm md:text-base max-w-xl">
                Conoce nuestros contenidos editoriales sobre bienestar,
                actualidad y temas relevantes para las personas mayores
                y sus familias.
              </p>

              {latestEditorial?.title && (
                <p className="text-[#33404f] text-sm font-semibold">
                  Último Editorial: {latestEditorial.title}
                </p>
              )}

              <div className="pt-3">
                <Link to="/editorial">
                  <Button className="bg-[#33404f] hover:bg-[#3d4b5a] text-white font-bold px-6 py-3 rounded-xl text-sm">
                    Ver Editorial
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Actualidad Senior - Blog */}
      {blogArticles.length > 0 && (
        <section className="py-16 bg-white" data-testid="blog-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10">
              <h2 className="text-4xl font-bold text-[#33404f] mb-2">Actualidad Senior</h2>
              <p className="text-gray-500 text-lg">Noticias, beneficios y recomendaciones para inspirarte</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {blogArticles.slice(0, 6).map((article) => (
                <Link
                  key={article.slug}
                  to={`/blog/${article.slug}`}
                  className="group relative aspect-[4/3] rounded-2xl overflow-hidden"
                  data-testid={`blog-${article.slug}`}
                >
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-white font-bold text-lg leading-tight">{article.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/blog">
                <Button className="bg-[#33404f] text-white hover:bg-[#4a5568] px-8 py-4 text-base font-bold rounded-xl">
                  Ver Toda la Actualidad <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA for Providers */}
      <section className="py-20 bg-gradient-to-r from-[#00e7ff] to-[#00c4d4]">
        <div className="max-w-7xl mx-auto px-4 text-center text-[#33404f]">
          <h2 className="text-4xl font-bold mb-4">¿Ofreces servicios para adultos mayores?</h2>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">Únete a SeniorAdvisor y conecta con miles de familias que buscan servicios de calidad como el tuyo.</p>
          <Link to="/provider/register">
            <Button className="bg-[#33404f] text-white hover:bg-[#4a5568] px-12 py-6 text-lg font-bold rounded-xl">
              Registrar mi Servicio Gratis
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#33404f] py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-white text-sm opacity-80">
          SeniorAdvisor - Todos los derechos reservados
        </div>
      </footer>
    </div>
  );
};

const FeaturedGrid = ({ featured }) => {
  const FeaturedCard = ({ p }) => (
    <Link
      to={`/provider/${p.provider_id}`}
      className="group block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow"
      data-testid={`featured-${p.provider_id}`}
    >
      <div className="h-44 bg-gray-200 overflow-hidden">
        <img
          src={p.photos?.[0] || p.gallery?.[0]?.url || ''}
          alt={p.business_name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-1 mb-1.5">
          {[1,2,3,4,5].map(s => (
            <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(p.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
          ))}
          <span className="text-sm font-bold text-[#33404f] ml-1">{p.rating?.toFixed(1)}</span>
        </div>
        <h3 className="font-bold text-[#33404f] text-base mb-1 group-hover:text-[#00e7ff] transition-colors">{p.business_name}</h3>
        <p className="text-sm text-gray-500 flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5" /> {p.comuna}
        </p>
      </div>
    </Link>
  );

  return (
    <section className="py-16 bg-[#00e7ff]/60" data-testid="featured-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h2 className="text-4xl font-bold text-[#33404f] mb-1">Residencias y Cuidados Domiciliarios Destacados</h2>
          <p className="text-[#33404f]/60 text-lg">Los servicios mejor evaluados por las familias</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {featured.slice(0, 6).map((p) => (
            <FeaturedCard key={p.provider_id} p={p} />
          ))}
        </div>

        <div className="text-center mt-10">
          <Link to="/destacados">
            <Button 
              className="bg-[#33404f] text-[#00e7ff] hover:bg-[#4a5568] px-8 py-4 text-base font-bold rounded-xl"
              data-testid="toggle-all-featured"
            >
              Ver Todas las Destacadas
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Home;
